import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { usersInfiniteOptions, userByIdOptions } from "@dashboard/api/queryApi";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelectedCustomer } from "@dashboard/contexts/selectedCustomerContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSpinner,
    faCheck,
    faTimes,
    faSearch,
    faExclamationTriangle,
} from "@fortawesome/pro-solid-svg-icons";
import { cn, Popover, PopoverContent } from "frontend-components";

interface VirtualizedUserSelectorProps {
    value?: number | null;
    onChange: (userId: number | null) => void;
    label?: string;
    placeholder?: string;
    hasError?: boolean;
}

export function VirtualizedUserSelector({
    value,
    onChange,
    label,
    placeholder,
    hasError = false,
}: VirtualizedUserSelectorProps) {
    const { t } = useTranslation();
    const { selectedCustomerId: customerId } = useSelectedCustomer();

    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const anchorRef = useRef<HTMLDivElement>(null);
    const isClearing = useRef(false);
    const justSelectedUser = useRef<{ id: number; name: string } | null>(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    const { data: selectedUser, isError: isSelectedUserError } = useQuery({
        ...userByIdOptions({ id: value ?? null }),
        enabled: !!value,
    });

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        isError: isUsersError,
        error: usersError,
    } = useInfiniteQuery(
        usersInfiniteOptions(
            {
                search: isSearching ? debouncedSearch : "",
                customerId: customerId,
                pageSize: 20,
                type: ["NORMAL"],
            },
            !!customerId,
        ),
    );

    const allUsers = useMemo(() => {
        return data?.pages.flatMap((page) => page.data) ?? [];
    }, [data]);

    const totalCount = data?.pages[0]?.total ?? allUsers.length;
    const virtualCount = hasNextPage ? allUsers.length + 1 : allUsers.length;

    const rowVirtualizer = useVirtualizer({
        count: virtualCount,
        getScrollElement: () => scrollEl,
        estimateSize: () => 44,
        overscan: 5,
    });

    const virtualItems = rowVirtualizer.getVirtualItems();

    useEffect(() => {
        const lastItem = virtualItems[virtualItems.length - 1];
        if (!lastItem) return;

        if (lastItem.index >= allUsers.length - 1 && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [virtualItems, allUsers.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const handleSelect = useCallback(
        (userId: number) => {
            const user = allUsers.find((u) => u.id === userId);
            if (user) {
                justSelectedUser.current = {
                    id: userId,
                    name: `${user.firstName} ${user.lastName}`,
                };
            }
            onChange(userId);
            setIsOpen(false);
            setSearch("");
            setIsSearching(false);
        },
        [onChange, allUsers],
    );

    const handleClear = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            isClearing.current = true;
            justSelectedUser.current = null;
            onChange(null);
            setSearch("");
            setIsSearching(false);
            inputRef.current?.focus();
            setTimeout(() => {
                isClearing.current = false;
            }, 0);
        },
        [onChange],
    );

    const handleOpenChange = useCallback((open: boolean, details: { event: Event }) => {
        if (open) return;
        if (containerRef.current?.contains(details.event.target as Node)) return;
        setIsOpen(false);
        setSearch("");
        setIsSearching(false);
    }, []);

    const handleInputFocus = useCallback(() => {
        setIsOpen(true);
    }, []);

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;
            setSearch(newValue);
            setIsSearching(true);
            if (!isOpen) setIsOpen(true);
        },
        [isOpen],
    );

    const selectedUserName = useMemo(() => {
        if (!value) return null;

        if (justSelectedUser.current && justSelectedUser.current.id === value) {
            return justSelectedUser.current.name;
        }

        if (isSelectedUserError) {
            return `User #${value}`;
        }
        if (selectedUser) {
            return `${selectedUser.firstName} ${selectedUser.lastName}`;
        }
        return null;
    }, [value, selectedUser, isSelectedUserError]);

    const inputDisplayValue = isOpen ? search : selectedUserName || "";
    const inputPlaceholder =
        isOpen && selectedUserName ? selectedUserName : placeholder || t("common.searchUsers");

    if (!customerId) {
        return (
            <div className="flex flex-col gap-1">
                {label && (
                    <span className="text-on-container text-sm font-medium uppercase tracking-wider">
                        {label}
                    </span>
                )}
                <div className="w-full px-3 py-2 bg-compendia-card text-on-container/50 border border-tertiary-outline rounded text-sm flex items-center gap-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="w-3 h-3" />
                    <span>{t("common.noCustomerContext")}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-1" ref={containerRef}>
            {label && (
                <span className="text-on-container text-sm font-medium uppercase tracking-wider">
                    {label}
                </span>
            )}

            <div className="relative" ref={anchorRef}>
                <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-on-container/50 w-3 h-3 pointer-events-none"
                />
                <input
                    ref={inputRef}
                    type="text"
                    value={inputDisplayValue}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    placeholder={inputPlaceholder}
                    className={cn(
                        "w-full pl-8 pr-10 py-2 text-on-container border rounded text-sm bg-white",
                        hasError
                            ? "border-chameleon-pink ring-chameleon-pink ring-1"
                            : "border-tertiary-outline",
                    )}
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                    {isOpen && !isLoading && !isUsersError && totalCount > 0 && (
                        <span className="text-xs text-primary font-medium">{totalCount}</span>
                    )}
                    {isLoading && !isFetchingNextPage && (
                        <FontAwesomeIcon
                            icon={faSpinner}
                            className="text-on-container/50 w-3 h-3 animate-spin"
                        />
                    )}
                    {!!value && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="text-on-container/50 hover:text-on-container p-1"
                            aria-label={t("common.clearSelection")}
                        >
                            <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                        </button>
                    )}
                </div>

                {isSelectedUserError && value && !isOpen && (
                    <div className="absolute left-0 right-0 top-full mt-1 text-xs text-chameleon-pink flex items-center gap-1">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="w-3 h-3" />
                        <span>{t("common.errorLoadingUser")}</span>
                    </div>
                )}

                <Popover open={isOpen} onOpenChange={handleOpenChange}>
                    <PopoverContent
                        anchor={anchorRef}
                        align="start"
                        initialFocus={false}
                        finalFocus={false}
                        className="w-(--anchor-width) p-0 gap-0 bg-container border border-tertiary-outline rounded shadow-lg ring-0 data-open:animate-none data-closed:animate-none"
                    >
                        <div ref={setScrollEl} className="max-h-36 overflow-y-auto">
                            {isUsersError ? (
                                <div className="py-8 text-center">
                                    <FontAwesomeIcon
                                        icon={faExclamationTriangle}
                                        className="w-5 h-5 text-chameleon-pink mb-2"
                                    />
                                    <div className="text-on-container/70 text-sm">
                                        {t("common.errorLoadingUsers")}
                                    </div>
                                    {usersError && (
                                        <div className="text-xs text-on-container/50 mt-1">
                                            {usersError.message}
                                        </div>
                                    )}
                                </div>
                            ) : isLoading ? (
                                <div className="flex items-center justify-center py-8">
                                    <FontAwesomeIcon
                                        icon={faSpinner}
                                        className="w-5 h-5 animate-spin text-on-container/50"
                                    />
                                </div>
                            ) : allUsers.length === 0 ? (
                                <div className="py-8 text-center text-on-container/50 text-sm">
                                    {t("common.noUsersFound")}
                                </div>
                            ) : (
                                <div
                                    style={{
                                        height: `${rowVirtualizer.getTotalSize()}px`,
                                        width: "100%",
                                        position: "relative",
                                    }}
                                >
                                    {virtualItems.map((virtualRow) => {
                                        const isLoaderRow = virtualRow.index >= allUsers.length;
                                        const userItem = isLoaderRow
                                            ? null
                                            : allUsers[virtualRow.index];
                                        const isSelected = userItem?.id === value;

                                        return (
                                            <div
                                                key={virtualRow.index}
                                                style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: "100%",
                                                    height: `${virtualRow.size}px`,
                                                    transform: `translateY(${virtualRow.start}px)`,
                                                }}
                                            >
                                                {isLoaderRow ? (
                                                    <div className="flex items-center justify-center h-full">
                                                        <FontAwesomeIcon
                                                            icon={faSpinner}
                                                            className="w-4 h-4 animate-spin text-on-container/50"
                                                        />
                                                    </div>
                                                ) : userItem ? (
                                                    <button
                                                        type="button"
                                                        className={cn(
                                                            "w-full h-full px-3 py-2 text-left flex items-center justify-between hover:bg-primary/10 transition-colors",
                                                            isSelected && "bg-primary/20",
                                                        )}
                                                        onClick={() => handleSelect(userItem.id)}
                                                    >
                                                        <span className="text-on-container text-sm truncate">
                                                            {userItem.firstName} {userItem.lastName}
                                                        </span>
                                                        {isSelected && (
                                                            <FontAwesomeIcon
                                                                icon={faCheck}
                                                                className="w-3 h-3 text-primary shrink-0"
                                                            />
                                                        )}
                                                    </button>
                                                ) : null}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </PopoverContent>
                </Popover>
            </div>
        </div>
    );
}
